import { action } from "typesafe-actions";
import {
    ICallCampaign,
    ICallCampaignData,
    IBulkCallCampaign,
    IAssignToLeadPayload,
    IAssignToAgentPayload
} from "./calling.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
    closeLoaderWithMessage,
    openLoaderWithMessage,
    showMessage
} from "../messages/messagesActions";
import { endPoints } from "../../constants/endPoints";
import { ISearchQueryParams } from "../common/common.types";
import { getSearchQueryV2 } from "../common/helpers";

// =========================================================
// 1. CALL CAMPAIGN ACTIONS (MASTER)
// =========================================================

export const FETCH_CALL_CAMPAIGN_LIST_PROGRESS = "FETCH_CALL_CAMPAIGN_LIST_PROGRESS" as const;
export const FETCH_CALL_CAMPAIGN_LIST_SUCCESS = "FETCH_CALL_CAMPAIGN_LIST_SUCCESS" as const;
export const FETCH_CALL_CAMPAIGN_LIST_FAILED = "FETCH_CALL_CAMPAIGN_LIST_FAILED" as const;

export const fetchCallCampaignListProgress = () => action(FETCH_CALL_CAMPAIGN_LIST_PROGRESS);
export const fetchCallCampaignListSuccess = (list: ICallCampaign[], totalRecords: number) =>
    action(FETCH_CALL_CAMPAIGN_LIST_SUCCESS, { list, totalRecords });
export const fetchCallCampaignListFailed = () => action(FETCH_CALL_CAMPAIGN_LIST_FAILED);

export const fetchCallCampaignListAsync = (queryParams: ISearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            dispatch(fetchCallCampaignListProgress());

            // FIX: Referencing provided file to solve 'undefined' URL issue
            const safeParams = {
                ...queryParams,

                // mandatory defaults
                page: queryParams.page ?? 1,
                rowsPerPage: queryParams.rowsPerPage ?? queryParams.limit ?? 25,

                // prevent status=undefined
                status: queryParams.status ?? "-1",
            };

            const url = getSearchQueryV2(safeParams);
            const res = await api.get(`${endPoints.calling.getCallCampaign}?${url}`);
            const data: ICallCampaign[] = res.data.data || [];
            const totalRecords = res.data.totalRecords || 0;
            dispatch(fetchCallCampaignListSuccess(data, totalRecords));
        } catch (err: any) {
            dispatch(fetchCallCampaignListFailed());
            dispatch(showMessage({ message: err?.response?.data?.message || "Failed to fetch call campaigns", type: "error", displayAs: "snackbar" }));
        }
    };

export const FETCH_CALL_CAMPAIGN_PROGRESS = "FETCH_CALL_CAMPAIGN_PROGRESS" as const;
export const FETCH_CALL_CAMPAIGN_SUCCESS = "FETCH_CALL_CAMPAIGN_SUCCESS" as const;
export const FETCH_CALL_CAMPAIGN_FAILED = "FETCH_CALL_CAMPAIGN_FAILED" as const;

export const fetchCallCampaignProgress = () => action(FETCH_CALL_CAMPAIGN_PROGRESS);
export const fetchCallCampaignSuccess = (callCampaign: ICallCampaign) => action(FETCH_CALL_CAMPAIGN_SUCCESS, { callCampaign });
export const fetchCallCampaignFailed = (errorMessage: string) => action(FETCH_CALL_CAMPAIGN_FAILED, { errorMessage });

export const fetchCallCampaignAsync = (callCampaignId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            dispatch(fetchCallCampaignProgress());
            const res = await api.get(`${endPoints.calling.getCallCampaign}?call_campaign_uuid=${callCampaignId}`);
            const data: ICallCampaign = res.data.data[0];
            dispatch(fetchCallCampaignSuccess(data));
        } catch (err: any) {
            dispatch(fetchCallCampaignFailed(err?.response?.data?.message || "Failed to fetch details"));
        }
    };

export const upsertCallCampaignAsync = (callCampaignData: ICallCampaign, onCallBack: (isSuccess?: boolean, data?: ICallCampaign) => void): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            dispatch(openLoaderWithMessage("Saving Call Campaign..."));

            // FIX: Strip forbidden fields as per provided reference file
            const {
                create_ts,
                insert_ts,
                created_by_name,
                created_by_uuid,
                modified_by_name,
                modified_by_uuid,
                ...payload
            } = callCampaignData;

            const hiddenForbidden = ["assigned_to_uuid", "assigned_to_name", "lead_uuid", "lead_name"];
            hiddenForbidden.forEach(field => delete (payload as any)[field]);

            const res = await api.post(endPoints.calling.upsertCallCampaign, payload);
            onCallBack(true, res.data.data);
            dispatch(showMessage({ message: res.data.message || "Saved successfully", type: "success", displayAs: "snackbar" }));
        } catch (err: any) {
            dispatch(showMessage({ message: err?.response?.data?.message || "Failed to save", type: "error", displayAs: "snackbar" }));
            onCallBack(false);
        } finally {
            dispatch(closeLoaderWithMessage());
        }
    };

// =========================================================
// 2. CALL CAMPAIGN DATA ACTIONS (LEADS/RECORDS)
// =========================================================

export const FETCH_CALL_CAMPAIGN_DATA_LIST_PROGRESS = "FETCH_CALL_CAMPAIGN_DATA_LIST_PROGRESS" as const;
export const FETCH_CALL_CAMPAIGN_DATA_LIST_SUCCESS = "FETCH_CALL_CAMPAIGN_DATA_LIST_SUCCESS" as const;
export const FETCH_CALL_CAMPAIGN_DATA_LIST_FAILED = "FETCH_CALL_CAMPAIGN_DATA_LIST_FAILED" as const;

export const fetchCallCampaignDataListProgress = () => action(FETCH_CALL_CAMPAIGN_DATA_LIST_PROGRESS);
export const fetchCallCampaignDataListSuccess = (list: ICallCampaignData[], totalRecords: number) => action(FETCH_CALL_CAMPAIGN_DATA_LIST_SUCCESS, { list, totalRecords });
export const fetchCallCampaignDataListFailed = () => action(FETCH_CALL_CAMPAIGN_DATA_LIST_FAILED);

export const fetchCallCampaignDataListAsync = (queryParams: ISearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            dispatch(fetchCallCampaignDataListProgress());

            // Normalize params
            const safeParams = {
                ...queryParams,

                // mandatory defaults
                page: queryParams.page ?? 1,
                rowsPerPage: queryParams.rowsPerPage ?? queryParams.limit ?? 25,

                // prevent status=undefined
                status: queryParams.status ?? "-1",
            };

            const url = getSearchQueryV2(safeParams);
            const res = await api.get(`${endPoints.calling.getCallCampaignData}?${url}`);
            dispatch(fetchCallCampaignDataListSuccess(res.data.data || [], res.data.totalRecords || 0));
        } catch (err: any) {
            dispatch(fetchCallCampaignDataListFailed());
            dispatch(showMessage({ message: err?.response?.data?.message || "Failed to fetch records", type: "error", displayAs: "snackbar" }));
        }
    };

export const FETCH_CALL_CAMPAIGN_DATA_PROGRESS = "FETCH_CALL_CAMPAIGN_DATA_PROGRESS" as const;
export const FETCH_CALL_CAMPAIGN_DATA_SUCCESS = "FETCH_CALL_CAMPAIGN_DATA_SUCCESS" as const;
export const FETCH_CALL_CAMPAIGN_DATA_FAILED = "FETCH_CALL_CAMPAIGN_DATA_FAILED" as const;

export const fetchCallCampaignDataProgress = () => action(FETCH_CALL_CAMPAIGN_DATA_PROGRESS);
export const fetchCallCampaignDataSuccess = (callCampaignData: ICallCampaignData) => action(FETCH_CALL_CAMPAIGN_DATA_SUCCESS, { callCampaignData });
export const fetchCallCampaignDataFailed = (errorMessage: string) => action(FETCH_CALL_CAMPAIGN_DATA_FAILED, { errorMessage });

export const fetchCallCampaignDataAsync = (callCampaignDataId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            dispatch(fetchCallCampaignDataProgress());
            const res = await api.get(`${endPoints.calling.getCallCampaignData}?call_campaign_data_uuid=${callCampaignDataId}`);
            const data: ICallCampaignData = res.data.data[0];
            dispatch(fetchCallCampaignDataSuccess(data));
        } catch (err: any) {
            dispatch(fetchCallCampaignDataFailed(err?.response?.data?.message || "Failed to fetch record"));
            dispatch(showMessage({ message: err?.response?.data?.message || "Failed to fetch record", type: "error", displayAs: "snackbar" }));
        }
    };

export const upsertCallCampaignDataAsync = (campaignData: ICallCampaignData, onCallBack: (isSuccess?: boolean, data?: ICallCampaignData) => void): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            dispatch(openLoaderWithMessage("Saving record..."));
            const {
                create_ts,
                insert_ts,
                modified_by_uuid,
                created_by_uuid,
                ...payload
            } = campaignData;

            // Define fields that backend might reject if they are "Invalid date"
            // ✅ FIXED: include scheduled_call_date
            const dateFields = [
                "membership_start_date",
                "membership_expiry_date",
                "next_day_call",
                "scheduled_call_date",
            ];

            // Safety Clean: Remove invalid dates and forbidden flags
            Object.keys(payload).forEach((key) => {
                const value = (payload as any)[key];

                // If it's a date field and it's not a valid date string, remove it
                if (dateFields.includes(key) && (value === "Invalid date" || !value)) {
                    delete (payload as any)[key];
                }
            });

            const hiddenForbidden = ["dnd_flag", "interested_flag"];
            hiddenForbidden.forEach(field => delete (payload as any)[field]);

            const res = await api.post(endPoints.calling.upsertCallCampaignData, payload);
            dispatch(showMessage({ message: res.data.message || "Saved successfully", type: "success", displayAs: "snackbar" }));
            onCallBack(true, res.data.data);
        } catch (err: any) {
            dispatch(showMessage({ message: err?.response?.data?.message || "Failed to save", type: "error", displayAs: "snackbar" }));
            onCallBack(false);
        } finally {
            dispatch(closeLoaderWithMessage());
        }
    };

// =========================================================
// 3. BULK & ASSIGNMENT ACTIONS
// =========================================================

export const INSERT_BULK_CALL_CAMPAIGN_PROGRESS = "INSERT_BULK_CALL_CAMPAIGN_PROGRESS" as const;
export const INSERT_BULK_CALL_CAMPAIGN_SUCCESS = "INSERT_BULK_CALL_CAMPAIGN_SUCCESS" as const;
export const INSERT_BULK_CALL_CAMPAIGN_FAILED = "INSERT_BULK_CALL_CAMPAIGN_FAILED" as const;

export const insertBulkCallCampaignProgress = () => action(INSERT_BULK_CALL_CAMPAIGN_PROGRESS);
export const insertBulkCallCampaignSuccess = (data: IBulkCallCampaign) => action(INSERT_BULK_CALL_CAMPAIGN_SUCCESS, { data });
export const insertBulkCallCampaignFailed = () => action(INSERT_BULK_CALL_CAMPAIGN_FAILED);

export const insertBulkCallCampaignAsync = (formData: FormData, onCallBack: (isSuccess?: boolean, data?: IBulkCallCampaign) => void): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {



        try {
            // Send as multipart/form-data
            // Note: Don't set Content-Type header manually - axios will set it automatically with boundary
            const res = await api.post(endPoints.calling.insertBulkCallCampaign, formData);

            const data: IBulkCallCampaign = res.data.data;
            dispatch(
                showMessage({
                    message: "Bulk campaign data added successfully",
                    type: "success",
                    displayAs: "snackbar",
                })
            );
            onCallBack(true, data);
        } catch (err: any) {
            dispatch(
                showMessage({
                    message: err?.response?.data?.message || "Failed to add bulk campaign data",
                    type: "error",
                    displayAs: "snackbar",
                })
            );
            onCallBack(false);
        }
    };

export const ASSIGN_TO_LEAD_PROGRESS = "ASSIGN_TO_LEAD_PROGRESS" as const;
export const ASSIGN_TO_LEAD_SUCCESS = "ASSIGN_TO_LEAD_SUCCESS" as const;
export const ASSIGN_TO_LEAD_FAILED = "ASSIGN_TO_LEAD_FAILED" as const;

export const assignToLeadProgress = () => action(ASSIGN_TO_LEAD_PROGRESS);
export const assignToLeadSuccess = () => action(ASSIGN_TO_LEAD_SUCCESS);
export const assignToLeadFailed = () => action(ASSIGN_TO_LEAD_FAILED);

export const assignCallCampaignDataToLeadAsync = (payload: IAssignToLeadPayload, onCallBack: (isSuccess: boolean) => void): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            dispatch(assignToLeadProgress());
            dispatch(openLoaderWithMessage("Assigning to lead..."));
            const res = await api.post(endPoints.calling.assignCallCampaignDataToLead, payload);
            dispatch(assignToLeadSuccess());
            dispatch(showMessage({ message: res.data.message || `Assigned to ${payload.lead_name}`, type: "success", displayAs: "snackbar" }));
            onCallBack(true);
        } catch (err: any) {
            dispatch(assignToLeadFailed());
            dispatch(showMessage({ message: err?.response?.data?.message || "Failed to assign lead", type: "error", displayAs: "snackbar" }));
            onCallBack(false);
        } finally {
            dispatch(closeLoaderWithMessage());
        }
    };

export const ASSIGN_TO_AGENT_PROGRESS = "ASSIGN_TO_AGENT_PROGRESS" as const;
export const ASSIGN_TO_AGENT_SUCCESS = "ASSIGN_TO_AGENT_SUCCESS" as const;
export const ASSIGN_TO_AGENT_FAILED = "ASSIGN_TO_AGENT_FAILED" as const;

export const assignToAgentProgress = () => action(ASSIGN_TO_AGENT_PROGRESS);
export const assignToAgentSuccess = () => action(ASSIGN_TO_AGENT_SUCCESS);
export const assignToAgentFailed = () => action(ASSIGN_TO_AGENT_FAILED);

export const assignCallCampaignDataToAgentAsync = (payload: IAssignToAgentPayload, onCallBack: (isSuccess: boolean) => void): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            dispatch(assignToAgentProgress());
            dispatch(openLoaderWithMessage("Assigning to agent..."));
            const res = await api.post(endPoints.calling.assignCallCampaignDataToAgent, payload);
            dispatch(assignToAgentSuccess());
            dispatch(showMessage({ message: res.data.message || `Assigned to ${payload.assigned_to_name}`, type: "success", displayAs: "snackbar" }));
            onCallBack(true);
        } catch (err: any) {
            dispatch(assignToAgentFailed());
            dispatch(showMessage({ message: err?.response?.data?.message || "Failed to assign agent", type: "error", displayAs: "snackbar" }));
            onCallBack(false);
        } finally {
            dispatch(closeLoaderWithMessage());
        }
    };

// =========================================================
// 4. CLEAR ACTIONS
// =========================================================

export const CLEAR_CALL_CAMPAIGN_STATE = "CLEAR_CALL_CAMPAIGN_STATE" as const;
export const CLEAR_CALL_CAMPAIGN = "CLEAR_CALL_CAMPAIGN" as const;
export const CLEAR_CALL_CAMPAIGN_DATA_STATE = "CLEAR_CALL_CAMPAIGN_DATA_STATE" as const;
export const CLEAR_CALL_CAMPAIGN_DATA = "CLEAR_CALL_CAMPAIGN_DATA" as const;
export const CLEAR_BULK_CAMPAIGN_STATE = "CLEAR_BULK_CAMPAIGN_STATE" as const;
export const CLEAR_ASSIGNMENT_STATUS = "CLEAR_ASSIGNMENT_STATUS" as const;

export const clearCallCampaignState = () => action(CLEAR_CALL_CAMPAIGN_STATE);
export const clearCallCampaign = () => action(CLEAR_CALL_CAMPAIGN);
export const clearCallCampaignDataState = () => action(CLEAR_CALL_CAMPAIGN_DATA_STATE);
export const clearCallCampaignData = () => action(CLEAR_CALL_CAMPAIGN_DATA);
export const clearBulkCampaignState = () => action(CLEAR_BULK_CAMPAIGN_STATE);
export const clearAssignmentStatus = () => action(CLEAR_ASSIGNMENT_STATUS);